import { useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { getAttribution } from "@/lib/attribution";
import { FIRM } from "@/lib/firm";
import { subscribeToAgentUpdate } from "@/lib/newsletter.functions";

const ROLE_OPTIONS = [
  { value: "agent", label: "Real estate agent" },
  { value: "broker", label: "Mortgage broker" },
  { value: "accountant", label: "Accountant" },
  { value: "financial-planner", label: "Financial planner" },
  { value: "other", label: "Something else" },
] as const;

const field =
  "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";
const labelCls = "block text-sm font-medium text-foreground";

export function AgentUpdateSignup({
  heading = "Get the fortnightly agent & broker update",
  compact = false,
}: {
  heading?: string;
  compact?: boolean;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      agency: String(fd.get("agency") ?? ""),
      role: String(fd.get("role") ?? "agent"),
      region: String(fd.get("region") ?? ""),
      ...getAttribution(pathname),
    };

    const nextErrors: { name?: string; email?: string } = {};
    if (payload.name.trim().length < 2) nextErrors.name = "Please tell us your name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payload.email.trim()))
      nextErrors.email = "Please enter a valid email address";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSending(true);
    try {
      await subscribeToAgentUpdate({ data: payload as never });
      setSent(true);
      form.reset();
      toast.success("You're on the list — the next update comes out on a Tuesday.");
    } catch {
      toast.error(`Something went wrong. Please email us at ${FIRM.email}.`);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="rounded-sm border-l-2 border-accent bg-secondary/50 p-7">
        <p className="font-serif text-2xl text-foreground">You&apos;re on the list</p>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Check your inbox — we&apos;ve sent a short confirmation with what to expect. If anything
          comes up before the next issue, call us on {FIRM.phone}.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-sm border border-border bg-card p-6 sm:p-7"
      aria-label="Agent and broker update signup"
    >
      <h2 className="font-serif text-2xl text-foreground">{heading}</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        One short email every second Tuesday on a Queensland property or estate issue that&apos;s
        affecting deals right now. No pitches, no filler — unsubscribe any time.
      </p>

      <div className={`mt-6 grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label className={labelCls} htmlFor="au-name">
            Name
          </label>
          <input id="au-name" name="name" className={`mt-1.5 ${field}`} autoComplete="name" />
          {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="au-email">
            Email
          </label>
          <input
            id="au-email"
            name="email"
            type="email"
            className={`mt-1.5 ${field}`}
            autoComplete="email"
          />
          {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="au-agency">
            Agency or business <span className="text-muted-foreground">(optional)</span>
          </label>
          <input id="au-agency" name="agency" className={`mt-1.5 ${field}`} />
        </div>
        <div>
          <label className={labelCls} htmlFor="au-role">
            What you do
          </label>
          <select id="au-role" name="role" className={`mt-1.5 ${field}`} defaultValue="agent">
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className={compact ? "" : "sm:col-span-2"}>
          <label className={labelCls} htmlFor="au-region">
            Where you work <span className="text-muted-foreground">(optional)</span>
          </label>
          <input
            id="au-region"
            name="region"
            placeholder="Caloundra, Redcliffe, North Lakes…"
            className={`mt-1.5 ${field}`}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={sending}
        className="mt-6 inline-flex items-center justify-center rounded-sm bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {sending ? "Adding you…" : "Subscribe"}
      </button>
      <p className="mt-3 text-xs text-muted-foreground">
        We use your details only to send the update. We never sell or share the list.
      </p>
    </form>
  );
}
